/**
 * Pipeline helpers: stage and status wording, dates, and what a lead becomes when it is
 * converted. Dates are plain calendar days ("2026-11-15") in the user's own time zone; the
 * service never guesses a time zone for them.
 */
export const SIDES = [
  { value: 'BUY', label: 'Buyers', noun: 'buyer' },
  { value: 'SELL', label: 'Sellers', noun: 'seller' },
];

export const sideNoun = (side) => (side === 'SELL' ? 'seller' : 'buyer');

export const STAGE_LABELS = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  ENGAGED: 'Engaged',
  APPOINTMENT_SET: 'Appointment set',
  SHOWING: 'Showing',
  OFFER: 'Offer',
  LISTING_PRESENTATION: 'Listing presentation',
  LISTING_SIGNED: 'Listing signed',
};

/** What to do next at each stage. The date comes from the lead's follow-up, which the service plans automatically. */
export const NEXT_ACTIONS = {
  NEW: 'Make first contact',
  CONTACTED: 'Follow up and learn what they need',
  ENGAGED: 'Book a meeting or appointment',
  APPOINTMENT_SET: 'Prepare for and attend the appointment',
  SHOWING: 'Follow up after the showing',
  OFFER: 'Follow up on the offer',
  LISTING_PRESENTATION: 'Follow up on the listing presentation',
  LISTING_SIGNED: 'Check in on the listing',
};

export const nextActionText = (stage) => NEXT_ACTIONS[stage] || '';

export const STAGE_DESCRIPTIONS = {
  NEW: 'A new lead. Make first contact quickly.',
  CONTACTED: 'You have been in touch. Learn what they need.',
  ENGAGED: 'They are actively talking with you.',
  APPOINTMENT_SET: 'A meeting is booked.',
  SHOWING: 'Touring homes together.',
  OFFER: 'An offer is being prepared or negotiated.',
  LISTING_PRESENTATION: 'Presenting your plan to the seller.',
  LISTING_SIGNED: 'The listing agreement is signed.',
};

export const stageLabel = (stage) => STAGE_LABELS[stage] || stage || '';

/** The columns for a side, in order. Mirrors the service, which is the authority. */
export const stagesFor = (side) => (side === 'SELL'
  ? ['NEW', 'CONTACTED', 'ENGAGED', 'APPOINTMENT_SET', 'LISTING_PRESENTATION', 'LISTING_SIGNED']
  : ['NEW', 'CONTACTED', 'ENGAGED', 'APPOINTMENT_SET', 'SHOWING', 'OFFER']);

export const STATUS_LABELS = { OPEN: 'Open', NURTURING: 'Nurturing', LOST: 'Lost', WON: 'Won' };
export const statusLabel = (status) => STATUS_LABELS[status] || status || '';

export const LOST_REASONS = [
  { value: 'WENT_WITH_ANOTHER_AGENT', label: 'Went with another agent' },
  { value: 'CHANGED_PLANS', label: 'Changed plans' },
  { value: 'NOT_READY', label: 'Not ready' },
  { value: 'UNRESPONSIVE', label: 'Stopped responding' },
  { value: 'PRICE', label: 'Price' },
  { value: 'FINANCING', label: 'Financing' },
  { value: 'OTHER', label: 'Other' },
];
export const lostReasonLabel = (value) => LOST_REASONS.find((r) => r.value === value)?.label || value || '';

export const TASK_TYPES = [
  { value: 'CALL', label: 'Call' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'MEETING', label: 'Meeting' },
  { value: 'SHOWING', label: 'Showing' },
  { value: 'TASK', label: 'To do' },
];
export const taskTypeLabel = (value) => TASK_TYPES.find((t) => t.value === value)?.label || '';

/** "Call the lead (today)" for a lead's next task; falls back to the stage's general next step. */
export const nextStepText = (nextTask, stage, today = localDateString()) => {
  if (nextTask) return `${nextTask.title} (${dayPhrase(nextTask.dueDate, today)})`;
  return nextActionText(stage);
};

export const ACTIVITY_TYPES = [
  { value: 'CALL', label: 'Call' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'MEETING', label: 'Meeting' },
  { value: 'SHOWING', label: 'Showing' },
  { value: 'NOTE', label: 'Note' },
];

const pad = (n) => String(n).padStart(2, '0');

/** Today (or any date) as yyyy-MM-dd in the browser's own time zone. */
export const localDateString = (date = new Date()) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** "Nov 15" (adds the year when it is not the current one). Empty for no date. */
export const formatDay = (day, now = new Date()) => {
  if (!day) return '';
  const [y, m, d] = String(day).split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  const opts = y === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
  return date.toLocaleDateString(undefined, opts);
};

/** True when the follow-up day is before today. */
export const isOverdue = (day, today = localDateString()) => !!day && day < today;

/** "Today", "Tomorrow", "3 days ago" or the date, for a follow-up day. */
export const followUpText = (day, today = localDateString()) => {
  if (!day) return '';
  if (day === today) return 'Today';
  const [y, m, d] = day.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  const diff = Math.round((new Date(y, m - 1, d) - new Date(ty, tm - 1, td)) / 86400000);
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff < 0 && diff >= -30) return `${-diff} days ago`;
  return formatDay(day);
};

/** Like followUpText but for the middle of a sentence: "today", "3 days ago", but a date stays "Nov 4". */
export const dayPhrase = (day, today = localDateString()) => {
  const text = followUpText(day, today);
  return /^(Today|Tomorrow|Yesterday|\d+ days ago)$/.test(text) ? text.toLowerCase() : text;
};

/** "3 days ago" / "today" for an ISO instant, by calendar day in the browser's time zone. Empty when there is none. */
export const sinceText = (iso, now = new Date()) => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  // Whole calendar days on the user's own clock (not 24-hour blocks), so 9 pm yesterday is "yesterday".
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(then)) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

export const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** One line describing a system entry on the timeline. */
export const describeEvent = (entry) => {
  const d = entry.data || {};
  const side = entry.side ? ` (${sideNoun(entry.side)})` : '';
  switch (entry.type) {
    case 'STAGE_CHANGED':
      return `Moved from ${stageLabel(d.from)} to ${stageLabel(d.to)}${side}${d.auto ? ' after a contact was logged' : ''}`;
    case 'STATUS_CHANGED':
      return d.to === 'LOST'
        ? `Marked lost${side}: ${lostReasonLabel(d.reason)}`
        : `Status changed from ${statusLabel(d.from)} to ${statusLabel(d.to)}${side}`;
    case 'CONVERTED':
      return `Converted to a transaction${side}`;
    default:
      return '';
  }
};

/** What a new transaction for this lead is called until the agent renames it. */
export const defaultTransactionName = (person, side, displayName) => {
  const street = person?.propertyAddress?.line1;
  if (side === 'SELL') return street ? `${street} (listing)` : `${displayName} - Listing`;
  return `${displayName} - Purchase`;
};

/** The body for POST /api/v1/transactions: a Prospect (status PENDING), the same as the manual create. */
export const transactionRequestFor = (side, { name, address }) => {
  const body = side === 'SELL'
    ? { type: 'SALE', side: 'LISTING', transactionName: name, status: 'PENDING' }
    : { type: 'PURCHASE', side: 'BUYING', transactionName: name, status: 'PENDING' };
  if (address) {
    if (address.line1) body.propertyAddress = address.line1;
    if (address.city) body.propertyCity = address.city;
    if (address.state) body.propertyState = address.state;
    if (address.zip) body.propertyZip = address.zip;
  }
  return body;
};

/**
 * The reference party that stands for the client on the transaction: the client's name, email and
 * phone and nothing else (no notes, tags or other contacts leave the CRM).
 */
export const referencePartyFor = (side, { name, email, phone, toPhone = (v) => v }) => ({
  name,
  ...(email && { email }),
  ...(phone && { phone: toPhone(phone) }),
  role: side === 'SELL' ? 'SELLER' : 'BUYER',
  referenceOnly: true,
});
